import { fmtR, esc, capitalise } from './utils.js';
import { showAuthModal, handleLogout, getAuthState, updateAuthUI } from './auth.js';

function getPublicFlags() {
  return window.medipricePublicFlags && typeof window.medipricePublicFlags === 'object'
    ? window.medipricePublicFlags
    : {};
}

function referralProgrammeActive(flags) {
  const per = Number(flags.referralCreditsPerReferral);
  const max = Number(flags.referralMaxCredits);
  return Number.isFinite(per) && per > 0 && Number.isFinite(max) && max > 0;
}

/** Last successful result — kept for the PDF download button. */
let lastResultData = null;

// ─── PDF / print generation ───────────────────────────────────────────────────

/** Safe HTML escape for the print window (no DOM available). */
function escHtml(s) {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function generatePrintHtml(data) {
  const items = data.items || [];
  const date  = new Date().toLocaleDateString('en-ZA', {
    year: 'numeric', month: 'long', day: 'numeric',
  });
  const schemeName =
    data.scheme && data.scheme !== 'none'
      ? data.scheme.charAt(0).toUpperCase() + data.scheme.slice(1)
      : 'No scheme selected';
  const modelName = {
    private: 'Private pharmacy (with highest pharmacy fee)',
    network: 'Network / DSP pharmacy (scheme pays dispensing fee)',
    state:   'State / government facility (free)',
  }[data.pharmacy_model] || 'Private pharmacy (with highest pharmacy fee)';
  const fmtRp = (n) => 'R\u202f' + Number(n || 0).toFixed(2);
  const showSchemeCol =
    data.scheme && data.scheme !== 'none' && data.scheme !== 'all';
  const model     = data.pharmacy_model || 'private';
  const hasScheme = showSchemeCol;

  // Best-price summary box
  let bestBoxHtml = '';
  if (data.best_pricing) {
    const bp = data.best_pricing;
    const title = data.scheme === 'all' ? 'Best Price Total' : 'Estimated Total';
    bestBoxHtml = `
      <div class="best-box">
        <div class="best-box__label">${title}</div>
        <div class="best-box__val">${fmtRp(bp.total_cost)}</div>
        <div class="best-box__note">${bp.matched_count} of ${items.length} medicines matched</div>
      </div>`;
  }

  // Single compact table: script item | medicine | you pay | scheme (if any)
  const schemeColHead = showSchemeCol ? '<th>Scheme</th>' : '';

  const allRows = [];
  items.forEach((item, i) => {
    const matches = (item.matches || []).slice(0, 5);
    const uncertain = typeof item.confidence === 'number' && item.confidence < 0.7;
    const rawLine = escHtml(item.raw_line || 'Unknown script item') + (uncertain ? ' <span class="tag tag--u">?</span>' : '');

    let bestGenericOop = Infinity;
    let bestOriginalOop = Infinity;
    if (data.best_pricing && matches.length > 0) {
      // Pass 1: Exact form matches
      matches.forEach(m => {
        if (m.patient_oop != null && m.form_match) {
          if (m.is_generic && m.patient_oop < bestGenericOop) bestGenericOop = m.patient_oop;
          if (!m.is_generic && m.patient_oop < bestOriginalOop) bestOriginalOop = m.patient_oop;
        }
      });
      // Pass 2: Fallback to alternative only if no exact match found
      if (bestGenericOop === Infinity) {
        matches.forEach(m => {
          if (m.patient_oop != null && m.is_generic && m.is_alternative && m.patient_oop < bestGenericOop) {
            bestGenericOop = m.patient_oop;
          }
        });
      }
      if (bestOriginalOop === Infinity) {
        matches.forEach(m => {
          if (m.patient_oop != null && !m.is_generic && m.is_alternative && m.patient_oop < bestOriginalOop) {
            bestOriginalOop = m.patient_oop;
          }
        });
      }
    }

    if (!matches.length) {
      allRows.push(`<tr class="item-row"><td class="script-line-cell" colspan="${showSchemeCol ? 4 : 3}">${rawLine}</td></tr><tr><td colspan="${showSchemeCol ? 4 : 3}" class="empty">No clear matches found</td></tr>`);
      return;
    }
    matches.forEach((m, j) => {
      const tags = [];
      if (m.is_generic) {
        tags.push('<span class="tag tag--g">Generic</span>');
      } else {
        tags.push('<span class="tag tag--o">Original</span>');
      }
      
      if (model === 'state') tags.push('<span class="tag tag--c">Free</span>');
      else if (m.co_pay === 0 && m.scheme_price != null) tags.push(`<span class="tag tag--c">${model === 'network' ? 'DSP' : 'Covered'}</span>`);
      else if (m.co_pay != null && m.co_pay > 0) tags.push(`<span class="tag tag--p">~${fmtRp(m.co_pay)}</span>`);

      const schemeCell = showSchemeCol ? `<td class="num">${m.scheme_price != null ? fmtRp(m.scheme_price) : '-'}</td>` : '';
      let mainVal = model === 'state' ? 'Free' : fmtRp(m.patient_oop ?? m.total ?? m.sep);
      if (m.qty_note) mainVal += ` <span class="sub">${escHtml(m.qty_note)}</span>`;
      let breakdown = '';
      if (model !== 'state' && m.dispensing_fee > 0) {
        const feeText = (model === 'network' && hasScheme) ? 'fee paid' : `+${fmtRp(m.dispensing_fee)}`;
        breakdown = `<span class="sub">${fmtRp(m.sep)} ${feeText}</span>`;
      }
      const prescribedCell = j === 0 ? `<td class="script-line-cell" rowspan="${matches.length}">${rawLine}</td>` : '';
      
      const isBestGen = data.best_pricing && m.is_generic && m.patient_oop === bestGenericOop && bestGenericOop < Infinity;
      const isBestOrg = data.best_pricing && !m.is_generic && m.patient_oop === bestOriginalOop && bestOriginalOop < Infinity;
      
      const rowStyle = (isBestGen || isBestOrg) ? 'background:rgba(240,25,95,0.06); font-weight:bold;' : '';
      if (isBestGen) tags.push('<span class="tag tag--best">Best Generic Match</span>');
      if (isBestOrg) tags.push('<span class="tag tag--best-org">Best Original Match</span>');
      if (m.is_alternative) {
        const formLabel = m.dosage_form ? ` (${capitalise(m.dosage_form)})` : '';
        tags.push(`<span class="tag tag--u">Different dosage form${formLabel}</span>`);
      }

      allRows.push(`<tr class="item-row" style="${rowStyle}">${j === 0 ? prescribedCell : ''}<td>${escHtml(m.name || 'Unknown')}${m.dosage_form ? `<span class="form">${escHtml(m.dosage_form)}</span>` : ''}${tags.join('')}</td><td class="num">${mainVal}${breakdown ? `<br>${breakdown}` : ''}</td>${schemeCell}</tr>`);
    });
  });
  const itemsHtml = `<table class="pdf-table"><thead><tr><th>Script Item</th><th>Medicine Result</th><th>Patient Pays</th>${schemeColHead}</tr></thead><tbody>${allRows.join('')}</tbody></table>`;

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width,initial-scale=1">
<title>MediPrice - Script Analysis ${date}</title>
<link rel="preconnect" href="https://fonts.googleapis.com">
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
<link href="https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap" rel="stylesheet">
<style>
  *{box-sizing:border-box;margin:0;padding:0}
  body{font-family:'Plus Jakarta Sans',system-ui,-apple-system,sans-serif;font-size:11px;color:#180910;background:#fff;padding:16px 20px;max-width:700px;margin:0 auto}
  .hdr{display:flex;justify-content:space-between;align-items:flex-end;margin-bottom:12px;padding-bottom:10px;border-bottom:2px solid #f0195f}
  .logo{font-size:20px;font-weight:800;color:#f0195f;letter-spacing:-0.03em}.logo span{color:#180910}
  .meta{text-align:right;font-size:10px;color:#8b5e70;line-height:1.5}
  .meta strong{color:#180910}
  .best-box{text-align:center;background:#fef7f9;border:1px solid rgba(233,30,99,0.18);border-radius:12px;padding:12px;margin-bottom:16px}
  .best-box__label{font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.06em;color:#8b5e70;margin-bottom:4px}
  .best-box__val{font-size:24px;font-weight:800;color:#f0195f;line-height:1.1}
  .best-box__note{font-size:10px;color:#8b5e70;margin-top:4px}
  .pdf-table{width:100%;border-collapse:collapse;font-size:10px}
  .pdf-table th{text-align:left;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.05em;color:#8b5e70;padding:6px 8px;border-bottom:1.5px solid rgba(233,30,99,0.08);background:#fef7f9}
  .pdf-table td{padding:8px;border-bottom:1px solid rgba(233,30,99,0.05);vertical-align:top;line-height:1.4}
  .pdf-table .script-line-cell{font-weight:700;color:#180910;max-width:160px}
  .pdf-table .num{text-align:right;font-variant-numeric:tabular-nums;font-weight:700;color:#f0195f}
  .pdf-table .sub{font-size:9px;color:#8b5e70;display:block;margin-top:2px}
  .form{display:inline-block;font-size:8px;background:rgba(0,0,0,0.04);color:#4a2535;border-radius:4px;padding:1px 4px;margin-left:4px;vertical-align:middle;font-weight:600}
  .empty{color:#8b5e70;font-style:italic;font-size:10px;padding:12px;text-align:center}
  .tag{display:inline-flex;font-size:8px;font-weight:700;text-transform:uppercase;border-radius:999px;padding:1px 5px;margin-left:5px;vertical-align:middle}
  .tag--g{background:#ffe0ec;color:#c41250}
  .tag--o{background:#e3f2fd;color:#1565c0}
  .tag--c{background:#e3f2fd;color:#1565c0}
  .tag--p{background:#fff3e0;color:#ef6c00}
  .tag--u{background:#fafafa;color:#8b5e70;border:1px solid rgba(233,30,99,0.08)}
  .tag--best{background:#f0195f;color:#fff;box-shadow:0 2px 4px rgba(240,25,95,0.2)}
  .tag--best-org{background:#631ee9;color:#fff;box-shadow:0 2px 4px rgba(99,30,233,0.2)}
  .disclaimer{margin-top:20px;padding:12px 16px;background:#fffde7;border:1px solid #f9a825;border-radius:12px;font-size:10px;color:#4a2535;line-height:1.6}
  .disclaimer strong{color:#180910}
  .actions{display:flex;gap:12px;margin-top:24px;justify-content:center}
  .btn{padding:10px 24px;background:#f0195f;color:#fff;border:none;border-radius:999px;font-size:12px;font-weight:700;cursor:pointer;transition:transform 0.1s}
  .btn:hover{background:#c41250;transform:translateY(-1px)}
  .btn-o{background:transparent;color:#f0195f;border:2px solid #f0195f}
  .btn-o:hover{background:#fef7f9}
  @media print{.actions{display:none!important}body{padding:0}.pdf-table{page-break-inside:auto}}
</style>
</head>
<body>
  <div class="hdr">
    <div class="logo">Medi<span>Price</span></div>
    <div class="meta">${escHtml(date)} | ${escHtml(schemeName)} | ${escHtml(modelName)}</div>
  </div>
  ${bestBoxHtml}
  ${itemsHtml}
  <div class="disclaimer">
    <strong>Important:</strong> ${escHtml(data.disclaimer || 'This is not a prescription. Discuss with your pharmacist and doctor.')}
    Fees shown are the highest allowed; your pharmacy may charge less.
  </div>
  <div class="actions">
    <button class="btn" onclick="window.print()">Print / Save as PDF</button>
    <button class="btn btn-o" onclick="window.close()">Close</button>
  </div>
</body>
</html>`;
}

const SCRIPT_ALLOWED_TYPES = new Set([
  'image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif', 'application/pdf',
]);
const SCRIPT_MAX_BYTES = 4 * 1024 * 1024;

export async function readFileAsBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result || '';
      const base64 = String(result).split(',').pop() || '';
      resolve(base64);
    };
    reader.onerror = () => reject(reader.error || new Error('Could not read file'));
    reader.readAsDataURL(file);
  });
}

/** Pro = unlimited; otherwise need at least one Script Check credit (packs, referrals, promos). */
function isScriptUploadAllowed() {
  const { loggedIn, isPro, checks } = getAuthState();
  return loggedIn && (isPro || (Number.isFinite(checks) && checks > 0));
}

function updateChecksNote() {
  const el = document.getElementById('script-checks-note');
  if (!el) return;

  const isPro = localStorage.getItem('mediprice_pro') === 'true';
  const checks = parseInt(localStorage.getItem('mediprice_checks') || '0', 10);

  if (isPro) {
    el.textContent = 'Pro: unlimited runs — no credits deducted.';
    return;
  }

  if (Number.isFinite(checks) && checks > 0) {
    el.textContent = `Each run uses 1 credit. You have ${checks} credit${checks === 1 ? '' : 's'} left.`;
    return;
  }

  el.textContent = 'Each run uses 1 credit (referrals, packs, or Pro unlimited). Sign in to use your balance.';
}

function updateScriptAccessBanner() {
  const banner = document.getElementById('script-access-banner');
  const textEl = document.getElementById('script-access-banner-text');
  const signinBtn = document.getElementById('script-access-signin');
  const pricingLink = document.getElementById('script-access-pricing');
  const referLink = document.getElementById('script-access-referral');
  const section = document.getElementById('script-checker');

  if (!banner || !textEl || !section) return;

  if (section.hidden) {
    banner.hidden = true;
    return;
  }

  const flags = getPublicFlags();
  const refLive = referralProgrammeActive(flags);
  const refPer = Number(flags.referralCreditsPerReferral);
  const refPerStr = Number.isFinite(refPer) && refPer > 0 ? String(refPer) : '';

  const token = localStorage.getItem('mediprice_token');
  const isPro = localStorage.getItem('mediprice_pro') === 'true';
  const checks = parseInt(localStorage.getItem('mediprice_checks') || '0', 10);

  // Sign in: only for logged-out users. If already signed in (even with 0 credits), use Pricing / Refer.
  const showSignIn = !token;
  if (signinBtn) {
    signinBtn.hidden = !showSignIn;
    signinBtn.setAttribute('aria-hidden', showSignIn ? 'false' : 'true');
  }
  if (pricingLink) pricingLink.hidden = isPro;
  if (referLink) referLink.hidden = !refLive;

  if (isPro) {
    textEl.textContent =
      'You’re on Pro — Script Checker is unlimited and we don’t deduct credits for each run.';
  } else if (!token) {
    let t =
      'Medicine search is always free. Script Checker uses your account: each successful run uses 1 credit from your balance. Sign in to use credits from referrals, on-demand packs, or upgrade to Pro for unlimited.';
    if (refLive && refPerStr) {
      t += ` Our referral programme can award up to ${refPerStr} credit${refPer === 1 ? '' : 's'} per friend who joins (subject to programme caps).`;
    } else if (refLive) {
      t += ' You can also earn referral credits when the programme is active — see Refer a friend below.';
    }
    textEl.textContent = t;
  } else if (checks > 0) {
    let t = `You’re signed in with ${checks} credit${checks === 1 ? '' : 's'} — each Script Checker run uses 1.`;
    if (refLive) {
      t += ' Earn more with referrals (amounts set by our current programme) or buy packs / Pro from Pricing.';
    } else {
      t += ' Buy more credits or Pro from Pricing when you need more runs.';
    }
    textEl.textContent = t;
  } else {
    let t =
      'You’re signed in but have no Script Check credits left. Buy an on-demand pack or go Pro for unlimited runs from Pricing.';
    if (refLive && refPerStr) {
      t += ` Or earn credits by referring friends — currently up to ${refPerStr} credit${refPer === 1 ? '' : 's'} per successful sign-up, within programme limits.`;
    } else if (refLive) {
      t += ' Or earn credits via Refer a friend (programme limits apply).';
    }
    textEl.textContent = t;
  }

  banner.hidden = false;
}

const SCRIPT_FILE_HINT_DEFAULT =
  'JPG, PNG, WebP, or PDF. Max 4 MB. We don\'t store your prescription.';

function syncScriptUploadGate(fileInput, submitBtn, fileHint, formBusy) {
  if (!fileInput) return;
  const allowed = isScriptUploadAllowed();
  const { loggedIn, isPro, checks } = getAuthState();

  fileInput.disabled = !allowed;
  fileInput.toggleAttribute('required', allowed);

  if (submitBtn) {
    submitBtn.disabled = formBusy || !allowed;
  }

  if (fileHint) {
    if (!allowed) {
      fileHint.classList.remove('script-form__note--error');
      if (!loggedIn) {
        fileHint.textContent =
          'Sign in to upload a prescription. You need Pro or Script Check credits (on-demand packs, referrals, or promos). Medicine search stays free.';
      } else if (!isPro && (!Number.isFinite(checks) || checks <= 0)) {
        fileHint.textContent =
          'Upload is available with Pro (unlimited) or when you have Script Check credits. Buy a pack or upgrade from Pricing.';
      }
    } else if (!fileHint.classList.contains('script-form__note--error')) {
      fileHint.textContent = SCRIPT_FILE_HINT_DEFAULT;
    }
  }

  const wrap = fileInput.closest('.script-form__file');
  if (wrap) wrap.classList.toggle('script-form__file--locked', !allowed);
}

function setLiveRegion(el, htmlFn) {
  el.innerHTML = '';
  requestAnimationFrame(() => { el.innerHTML = htmlFn(); });
}

export function renderScriptResults(container, data) {
  lastResultData = data;

  if (!data || !Array.isArray(data.items) || !data.items.length) {
    setLiveRegion(container, () => `
      <p class="script-results__empty">
        We could not find any medicines on this script.
        Try a clearer photo or ask your pharmacist for help.
      </p>`);
    container.removeAttribute('hidden');
    return;
  }

  let summaryHtml = '';
  const model = data.pharmacy_model || 'private';

  if (data.best_pricing) {
    const bp = data.best_pricing;
    const modelNote = {
      private: 'cheapest generics at a private pharmacy (medicine + dispensing fee)',
      network: 'cheapest generics at a network pharmacy (medicine only, scheme pays fee)',
      state:   'state/government facility (free)',
    }[model] || 'private pharmacy';

    const title = data.scheme === 'all' ? 'Best Price Combination' : 'Estimated Combination Total';
    const desc = data.scheme === 'all'
      ? `Estimated out-of-pocket cost for ${modelNote} using absolute cheapest generics.`
      : `Estimated out-of-pocket cost for ${modelNote} using medicines covered by your selection.`;

    summaryHtml = `
      <div class="bp-summary">
        <h3 class="bp-summary__title">${title}</h3>
        <p class="bp-summary__desc">${desc}</p>
        <div class="bp-grid">
          <div class="bp-scheme bp-scheme--winner" style="max-width:300px; margin:0 auto;">
            <div class="bp-scheme__name">Total Estimated Cost</div>
            <div class="bp-scheme__val">${fmtR(bp.total_cost)}</div>
          </div>
        </div>
      </div>
      <div class="script-results__divider">Matches found:</div>
    `;
  }

  const hasScheme  = data.scheme && data.scheme !== 'none' && data.scheme !== 'all';

  // Human-readable pharmacy model label for display
  const modelLabel = {
    private: 'Private pharmacy',
    network: 'Network / DSP pharmacy',
    state:   'State facility',
  }[model] || 'Private pharmacy';

  const isBestPriceMode = data.scheme === 'all' && data.best_pricing;

  const itemsHtml = data.items.map(item => {
    const matches   = Array.isArray(item.matches) ? item.matches : [];
    const uncertain = typeof item.confidence === 'number' && item.confidence < 0.7;

    let bestGenericOop = Infinity;
    let bestOriginalOop = Infinity;
    if (data.best_pricing && matches.length > 0) {
      // Pass 1: Exact form matches
      matches.forEach(m => {
        if (m.patient_oop != null && m.form_match) {
          if (m.is_generic && m.patient_oop < bestGenericOop) bestGenericOop = m.patient_oop;
          if (!m.is_generic && m.patient_oop < bestOriginalOop) bestOriginalOop = m.patient_oop;
        }
      });
      // Pass 2: Fallback to alternative only if no exact match found
      if (bestGenericOop === Infinity) {
        matches.forEach(m => {
          if (m.patient_oop != null && m.is_generic && m.is_alternative && m.patient_oop < bestGenericOop) {
            bestGenericOop = m.patient_oop;
          }
        });
      }
      if (bestOriginalOop === Infinity) {
        matches.forEach(m => {
          if (m.patient_oop != null && !m.is_generic && m.is_alternative && m.patient_oop < bestOriginalOop) {
            bestOriginalOop = m.patient_oop;
          }
        });
      }
    }

    const qtyBadge = item.prescribed_qty
      ? `<span class="script-item__badge script-item__badge--qty">× ${item.prescribed_qty}</span>`
      : '';
    const lineLabel = uncertain
      ? `${esc(item.raw_line || '')} ${qtyBadge} <span class="script-item__badge script-item__badge--uncertain">May be unclear</span>`
      : `${esc(item.raw_line || '')} ${qtyBadge}`;

    const matchHtml = matches.length
      ? matches.map(m => {
          const badges = [];
          if (m.is_generic) {
            badges.push('<span class="script-item__badge script-item__badge--generic">Generic</span>');
          } else {
            badges.push('<span class="script-item__badge script-item__badge--original">Original</span>');
          }
          if (m.is_alternative) {
            const formLabel = m.dosage_form ? ` (${capitalise(m.dosage_form)})` : '';
            badges.push(`<span class="script-item__badge script-item__badge--uncertain">Different dosage form${formLabel}</span>`);
          }
          if (m.price_adjusted) badges.push('<span class="script-item__badge script-item__badge--adjusted">Adjusted for pack size</span>');

          // ── Coverage / co-pay badge ─────────────────────────────────────────
          if (model === 'state') {
            badges.push('<span class="script-item__badge script-item__badge--covered">Free at state facility</span>');
          } else if (m.scheme_price != null) {
            if (m.co_pay === 0 && model === 'network') {
              badges.push('<span class="script-item__badge script-item__badge--covered">Fully covered (DSP)*</span>');
            } else if (m.co_pay === 0) {
              badges.push('<span class="script-item__badge script-item__badge--covered">Medicine covered (pay fee only)*</span>');
            } else if (m.co_pay != null && m.co_pay > 0) {
              badges.push(`<span class="script-item__badge script-item__badge--copay">Medicine co-pay ~${fmtR(m.co_pay)}</span>`);
            }
          }

          // ── Quantity note ───────────────────────────────────────────────────
          const qtyNote = m.qty_note
            ? `<span class="script-item__match-price-note">${esc(m.qty_note)}</span>` : '';

          // ── Price block ─────────────────────────────────────────────────────
          // Always show patient_oop (what patient actually pays) as the headline.
          // Below it: a breakdown line showing SEP + dispensing fee.
          let mainPrice;
          if (model === 'state') {
            mainPrice = `<span class="script-item__match-price script-item__match-price--free">Free</span>`;
          } else {
            const oopVal = m.patient_oop != null ? fmtR(m.patient_oop) : fmtR(m.total ?? m.sep);
            mainPrice = `<span class="script-item__match-price">${oopVal} ${qtyNote}</span>`;
          }

          // Breakdown: SEP + dispensing fee = total (only if fee > 0)
          let breakdownHtml = '';
          if (model !== 'state' && m.dispensing_fee > 0) {
            const sepLine = m.qty_is_fractional
              ? `Official Price (SEP) ${fmtR(m.sep)} <span class="script-item__match-breakdown-note">(${esc(m.qty_note)})</span>`
              : `Official Price (SEP) ${fmtR(m.sep)}`;
            const feeLabel = (model === 'network' && hasScheme)
              ? `+ fee (scheme covered)`
              : `+ pharmacy fee ${fmtR(m.dispensing_fee)}`;
            breakdownHtml = `<span class="script-item__match-breakdown">${sepLine} ${feeLabel}</span>`;

            // For fractional packs also show full-pack reference
            if (m.qty_is_fractional) {
              breakdownHtml += `<span class="script-item__match-pack-price">${fmtR(m.sep_pack)} full pack</span>`;
            }
          } else if (m.qty_is_fractional) {
            // No fee (state/network-covered) but still show proportional breakdown
            breakdownHtml = `<span class="script-item__match-pack-price">${fmtR(m.sep_pack)} full pack</span>`;
          }

          // Scheme covers line (when applicable)
          let schemeHtml = '';
          if (hasScheme && m.scheme_price != null && model !== 'state') {
            schemeHtml = `<span class="script-item__match-scheme">Scheme ref: ${fmtR(m.scheme_price)}</span>`;
          }

          const isBestGen = data.best_pricing && m.is_generic && m.patient_oop === bestGenericOop && bestGenericOop < Infinity;
          const isBestOrg = data.best_pricing && !m.is_generic && m.patient_oop === bestOriginalOop && bestOriginalOop < Infinity;
          
          if (isBestGen) badges.push('<span class="script-item__badge script-item__badge--best">Best Generic Match</span>');
          if (isBestOrg) badges.push('<span class="script-item__badge script-item__badge--best-org">Best Original Match</span>');
          
          const matchClass = (isBestGen || isBestOrg) ? 'script-item__match script-item__match--best' : 'script-item__match';

          return `
            <div class="${matchClass}">
              <span class="script-item__match-name">${esc(m.name || 'Unknown')} ${badges.join('')}</span>
              <span class="script-item__match-prices">
                ${mainPrice}
                ${breakdownHtml}
                ${schemeHtml}
              </span>
            </div>`;
        }).join('')
      : '<p class="script-item__match script-results__empty">No clear matches found for this line.</p>';

    return `
      <div class="script-item">
        <p class="script-item__line">${lineLabel}</p>
        ${matchHtml}
      </div>`;
  }).join('');

  setLiveRegion(container, () => {
    const dlIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>`;
    return `
    <div class="script-results__actions">
      <button type="button" class="script-results__dl-btn" id="script-dl-btn">
        ${dlIcon} Download PDF
      </button>
    </div>
    ${summaryHtml}
    ${itemsHtml}
    <p class="script-results__meta">
      Prices shown for: <strong>${esc(modelLabel)}</strong>. The pharmacy fee shown is the <strong>highest allowed</strong>; your pharmacy may charge you less.
      ${hasScheme ? ` · Medical aid: <strong>${esc(data.scheme.charAt(0).toUpperCase() + data.scheme.slice(1))}</strong>` : ''}
    </p>
    <p class="script-results__disclaimer">
      ${esc(data.disclaimer || 'This is not a prescription. Always discuss these options with your pharmacist and doctor.')}
      Coverage badges are estimates. Fees shown are the highest allowed; your pharmacy may charge less.
    </p>`;
  });
  container.removeAttribute('hidden');
}

export function initScriptChecker() {
  const form           = document.getElementById('script-form');
  const fileInput      = document.getElementById('script-file');
  const fileHint       = document.getElementById('script-file-hint');
  const schemeSelect   = document.getElementById('script-scheme');
  const pharmacySelect = document.getElementById('script-pharmacy');
  const submitBtn      = document.getElementById('script-submit-btn');
  const resultsEl      = document.getElementById('script-results');

  if (!form || !fileInput || !resultsEl) return;

  let formBusy = false;

  updateChecksNote();
  updateScriptAccessBanner();
  syncScriptUploadGate(fileInput, submitBtn, fileHint, formBusy);

  window.addEventListener('mediprice-public-flags', () => {
    updateScriptAccessBanner();
    updateChecksNote();
    syncScriptUploadGate(fileInput, submitBtn, fileHint, formBusy);
  });

  window.addEventListener('mediprice-auth-changed', () => {
    updateChecksNote();
    updateScriptAccessBanner();
    syncScriptUploadGate(fileInput, submitBtn, fileHint, formBusy);
  });

  document.getElementById('script-access-signin')?.addEventListener('click', () => showAuthModal());

  window.addEventListener('storage', (e) => {
    if (e.key === 'mediprice_pro' || e.key === 'mediprice_checks' || e.key === 'mediprice_token') {
      updateChecksNote();
      updateScriptAccessBanner();
      syncScriptUploadGate(fileInput, submitBtn, fileHint, formBusy);
    }
  });

  // Couple medical aid and pharmacy: Network only makes sense when a scheme is selected
  const pharmacyHint = document.getElementById('script-pharmacy-hint');
  const syncPharmacyToScheme = () => {
    const scheme = schemeSelect?.value || 'none';
    const networkOpt = pharmacySelect?.querySelector('option[value="network"]');
    if (!pharmacySelect || !networkOpt) return;
    const hasScheme = scheme !== 'none';
    networkOpt.disabled = !hasScheme;
    networkOpt.setAttribute('aria-disabled', !hasScheme ? 'true' : 'false');
    if (!hasScheme && pharmacySelect.value === 'network') {
      pharmacySelect.value = 'private';
    }
    if (pharmacyHint) {
      pharmacyHint.textContent = hasScheme
        ? 'Network = scheme pays pharmacy fee. Prices use the highest allowed fee; your pharmacy may charge less.'
        : 'Network pharmacy is for scheme members only. Select a medical aid to use it. Prices use the highest allowed fee; your pharmacy may charge less.';
    }
  };
  if (schemeSelect) schemeSelect.addEventListener('change', syncPharmacyToScheme);
  syncPharmacyToScheme(); // Initial state

  const setLoading = (on, label) => {
    formBusy = on;
    if (!submitBtn) return;
    submitBtn.classList.toggle('btn-primary--loading', on);
    submitBtn.disabled = on || !isScriptUploadAllowed();
    submitBtn.setAttribute('aria-busy', String(on));
    const labelEl = submitBtn.querySelector('.script-submit__label');
    if (labelEl) labelEl.textContent = on && label ? label : 'Check my script';
  };

  const showFileError = (msg) => {
    if (fileHint) {
      fileHint.textContent = msg;
      fileHint.classList.add('script-form__note--error');
    }
    fileInput.focus();
  };
  const clearFileError = () => {
    if (fileHint) fileHint.classList.remove('script-form__note--error');
    syncScriptUploadGate(fileInput, submitBtn, fileHint, formBusy);
  };

  const errorForStatus = (status) => {
    if (status === 400) return 'This file type isn\'t supported. Please upload a JPG, PNG, WebP, or PDF.';
    if (status === 402) return 'You need Pro or Script Check credits. Open Pricing to buy a pack or upgrade.';
    if (status === 403) return 'This feature is temporarily unavailable. Please try again later.';
    if (status === 413) return 'The file is too large. Please upload an image or PDF smaller than 4 MB.';
    if (status === 429) return 'Too many scans. Please wait a minute before trying again.';
    if (status === 502) return 'We couldn\'t read the script. Please try a clearer photo.';
    if (status === 504) return 'The request timed out. Please try again.';
    return 'Something went wrong. Please try again or use a different photo.';
  };

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    clearFileError();

    if (!isScriptUploadAllowed()) {
      const { loggedIn } = getAuthState();
      showAuthModal(
        loggedIn
          ? 'You need Pro or Script Check credits to run the checker. Open Pricing to buy a pack or upgrade.'
          : 'Sign in to use Script Checker. You need Pro or credits from packs or referrals.',
        false,
      );
      return;
    }

    const file = fileInput.files && fileInput.files[0];
    if (!file) { fileInput.focus(); return; }

    // Some mobile browsers (especially when sharing from gallery) set file.type = ''.
    // Allow these through and let the server infer the type; only reject explicit
    // unsupported types.
    const effectiveMimeType = file.type || 'image/jpeg';
    if (file.type && !SCRIPT_ALLOWED_TYPES.has(file.type)) {
      showFileError('Please upload a JPG, PNG, WebP, or PDF file.');
      return;
    }
    if (file.size > SCRIPT_MAX_BYTES) {
      showFileError('File is too large. Please upload an image smaller than 4 MB.');
      return;
    }

    resultsEl.setAttribute('hidden', '');
    resultsEl.innerHTML = '';
    setLoading(true, 'Reading photo…');

    try {
      const fileBase64     = await readFileAsBase64(file);
      const scheme         = schemeSelect   ? schemeSelect.value   : 'none';
      const pharmacyModel  = pharmacySelect ? pharmacySelect.value : 'private';

      setLoading(true, 'Checking script…');

      let token = localStorage.getItem('mediprice_token');
      if (!token) {
        showAuthModal(
          'Sign in to use Script Checker. Free search stays free; checks use credits from referrals, packs, or Pro.',
          false,
        );
        setLoading(false);
        return;
      }

      const res = await fetch('/api/parse-script', {
        method:  'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body:    JSON.stringify({ fileBase64, mimeType: effectiveMimeType, scheme, pharmacyModel }),
      });

      if (res.status === 401) {
        handleLogout();
        showAuthModal();
        setLoading(false);
        return;
      }

      if (!res.ok) {
        const msg = errorForStatus(res.status);
        setLiveRegion(resultsEl, () => `<p class="script-results__error">${esc(msg)}</p>`);
        resultsEl.removeAttribute('hidden');
        return;
      }

      const data = await res.json();
      if (
        data &&
        typeof data.checks_remaining === 'number' &&
        Number.isFinite(data.checks_remaining) &&
        localStorage.getItem('mediprice_pro') !== 'true'
      ) {
        localStorage.setItem('mediprice_checks', String(Math.max(0, data.checks_remaining)));
        updateAuthUI();
      }
      renderScriptResults(resultsEl, data);
      updateChecksNote();
      updateScriptAccessBanner();
      syncScriptUploadGate(fileInput, submitBtn, fileHint, formBusy);
    } catch (err) {
      console.error('[Script Checker]', err);
      const isNetwork = err?.name === 'TypeError' && err?.message?.includes('fetch');
      const msg = isNetwork
        ? 'We could not process this script. Please check your connection and try again.'
        : 'Something went wrong. Please try again or use a different photo.';
      setLiveRegion(resultsEl, () => `<p class="script-results__error">${esc(msg)}</p>`);
      resultsEl.removeAttribute('hidden');
    } finally {
      setLoading(false);
    }
  });

  // Download PDF — event delegated on resultsEl so it works after each render
  resultsEl.addEventListener('click', (e) => {
    if (!e.target.closest('#script-dl-btn')) return;
    if (!lastResultData) return;
    const win = window.open('', '_blank', 'width=860,height=720,scrollbars=yes,resizable=yes');
    if (!win) {
      // Popup blocked — fall back to a data-URI in the current tab
      const blob = new Blob([generatePrintHtml(lastResultData)], { type: 'text/html' });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href = url; a.download = 'mediprice-script.html'; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
      return;
    }
    win.document.write(generatePrintHtml(lastResultData));
    win.document.close();
    win.focus();
  });
}
